import {SolutionProductsResponse} from 'api/dataTypes';
import {ProductsGridState} from 'modules/ProductsGrid/containers/ProductsGridContainer/reducer';
import {
    ProductGridItemData,
    ProductsGridTotal,
    ProductsGridTotals,
} from 'modules/ProductsGrid/lib/types';

/**
 * Высчитываем суммарные данные по списку товаров
 * @param products список товаров
 * @returns суммарные данные по товарам
 */
export function calcProductsTotal(
    products: ProductGridItemData[],
): ProductsGridTotal {
    const total = products
        .filter((product) => !product.deleted)
        .reduce(
            (acc, product) => {
                acc.count += 1;
                acc.price.old += product.price.old * product.count;
                acc.price.current += product.price.current * product.count;

                return acc;
            },
            {
                count: 0,
                price: {
                    old: 0,
                    current: 0,
                },
            },
        );

    return total;
}

/**
 * Высчитываем суммарные данные по товарам. Вычисляет общую сумму для каждой группы,
 * а также общую сумму по всему списку товаров
 * @param param0
 * @returns
 */
export function calcProductsGridTotals({
    groupsEntities,
    productsEntities,
}: {
    groupsEntities: ProductsGridState['groups'];
    productsEntities: ProductsGridState['products'];
}): ProductsGridTotals {
    const result = {} as ProductsGridTotals;

    // Формируем массив данных по группам из мапы
    const groupsList = Object.values(groupsEntities);
    // Формируем мапу суммарных данных по группам
    const groupTotals: ProductsGridTotals['groups'] = groupsList.reduce(
        (acc, group) => {
            // составляем массив товаров текущей группы
            const products = group.products.map(
                (productId) => productsEntities[productId],
            );
            const additionalGroupProducts = group.additionalProducts.map(
                (productId) => productsEntities[productId],
            );
            const allProducts = [...products, ...additionalGroupProducts];

            // вычисляем сумарные данные по текущей группе
            const total = calcProductsTotal(allProducts);
            acc[group.id] = total;

            return acc;
        },
        {} as ProductsGridTotals['groups'],
    );
    result.groups = groupTotals;

    // из полученной мапы с сумарными данными по группам формируем массив
    const groupTotalsList = Object.values(groupTotals);
    // высчитываем суммарные данные по всему списку товаров
    const summaryTotal: ProductsGridTotals['summary'] = groupTotalsList.reduce(
        (acc, groupTotal) => {
            acc.count += groupTotal.count;
            acc.price.current += groupTotal.price.current;
            acc.price.old += groupTotal.price.old;

            return acc;
        },
        {
            count: 0,
            price: {
                old: 0,
                current: 0,
            },
        } as ProductsGridTotals['summary'],
    );
    result.summary = summaryTotal;

    return result;
}

/**
 * Формирует данные для генератора PDF в ввиде массива
 * с элементами `${product.id},${product.count}`
 */
export function prepareDataForPdfGenerator({
    groupsEntities,
    productsEntities,
    groupsIds,
    productIds,
}: {
    groupsEntities: ProductsGridState['groups'];
    productsEntities: ProductsGridState['products'];
    groupsIds: ProductsGridState['groupsIds'];
    productIds: ProductsGridState['productIds'];
}) {
    // массив строк в формате "ID,COUNT"
    const result: string[] = [];

    // Сначала проходим по группам
    groupsIds.forEach((groupId) => {
        const group = groupsEntities[groupId];
        if (!group) {
            return;
        }

        // Добавляем товары из группы
        group.products.forEach((productId) => {
            const product = productsEntities[productId];
            if (product && !product.deleted) {
                result.push(`${product.id},${product.count}`);
            }
        });

        // Добавляем дополнительные товары из группы
        group.additionalProducts.forEach((productId) => {
            const product = productsEntities[productId];
            if (product && !product.deleted) {
                result.push(`${product.id},${product.count}`);
            }
        });
    });

    // Добавляем товары, которые не входят в группы
    productIds.forEach((productId) => {
        const product = productsEntities[productId];
        if (product && !product.deleted) {
            result.push(`${product.id},${product.count}`);
        }
    });

    return result;
}

/**
 * Нормализация данных, из массива в мапу по id
 */
export function normalize<T extends {id: number}>(
    list: T[],
): Record<string, T> {
    return list.reduce(
        (acc, item) => {
            acc[item.id] = item;
            return acc;
        },
        {} as Record<string, T>,
    );
}

/**
 * Подготовить сырые данные полученные с бэка в формат для клиентского кода.
 * @param rawData сырые данные с бэка
 * @returns данные в формате локального стейта
 */
export function prepareInitialState(
    rawData: SolutionProductsResponse['data'],
): ProductsGridState {
    // Данные по группам. В каждой группе лежит список id товаров, название группы, ее id и тд
    const groupsEntities: ProductsGridState['groups'] = {};

    // Мапа товаров из всех групп по их id. Сразу можем добавить товары из общего списка
    let productsEntitiesFromGroups: ProductsGridState['products'] = {
        ...normalize(rawData.goods || []),
    };

    (rawData.groups || []).forEach((group, idx) => {
        // Записываем в мапу товары из текущей группы
        productsEntitiesFromGroups = {
            ...productsEntitiesFromGroups,
            ...normalize(group.goods),
            ...normalize(group.additionalGoods || []),
        };

        const groupId = group.goods_parent_id || idx;
        // Записываем данные по текущей группе
        groupsEntities[groupId] = {
            id: groupId,
            name: group.name,
            parentId: group.parent_id,

            // В списке товаров храним только список id
            additionalProducts: (group.additionalGoods || []).map(
                (product) => product.id,
            ),
            products: group.goods.map((product) => product.id),
        };
    });

    return {
        productIds: rawData.goods?.map((product) => product.id) || [],
        groupsIds:
            rawData.groups?.map((group, idx) => group.goods_parent_id || idx) ||
            [],
        products: productsEntitiesFromGroups,
        groups: groupsEntities,
    };
}
